import React from "react";
import {
  Box,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  Menu,
  MenuButton,
  MenuDivider,
  MenuList,
  MenuItem,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { HeaderWithStats } from "services/MetadataServices";
import { AppIcons } from "constants/icons";
import JobList from "modules/analyst/containers/JobList";
import { WorkflowFilter } from "modules/workflow/containers/WorkflowList";

export interface WorkflowListProps {
  // Data
  filteredJobs: Header[];
  isLoadingJobs: boolean;
  isLoadingAnalysts: boolean;
  analystsList: HeaderWithStats[];
  selectedAnalyst: Header | null;
  searchQuery: string;
  filterMode: WorkflowFilter;
  totalCount: number;
  liveCount: number;
  // Callbacks
  onSearchChange: (query: string) => void;
  onAnalystSelect: (analyst: Header | null) => void;
  onFilterChange: (mode: WorkflowFilter) => void;
  onJobClick: (job: Header) => void;
  onDefinitionClick: (job: Header) => void;
  onPastRunsClick: (job: Header) => void;
  onRefresh: () => void;
  onCreateNew: () => void;
}

const filterLabel: Record<WorkflowFilter, string> = {
  all: "All",
  live: "Live",
  paused: "Paused",
};

const WorkflowList: React.FC<WorkflowListProps> = ({
  filteredJobs,
  isLoadingJobs,
  isLoadingAnalysts,
  analystsList,
  selectedAnalyst,
  searchQuery,
  filterMode,
  totalCount,
  liveCount,
  onSearchChange,
  onAnalystSelect,
  onFilterChange,
  onJobClick,
  onDefinitionClick,
  onPastRunsClick,
  onRefresh,
  onCreateNew,
}) => {
  return (
    <>
      {/* Toolbar: search + filter + new */}
      <Box maxW="900px" w="100%" mx="auto" mb={3}>
        <HStack spacing={2} align="center" justify="space-between">
          {/* Search Bar */}
          <InputGroup size="md" maxW="50%">
            <InputLeftElement pointerEvents="none" height="full">
              <Icon as={AppIcons.search} boxSize={4} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search workflows..."
              value={searchQuery}
              onChange={e => onSearchChange(e.target.value)}
              bg="gray.50"
              border="1px"
              borderColor="gray.200"
              fontSize="sm"
              _hover={{ borderColor: "gray.300", bg: "white" }}
              _focus={{
                borderColor: "purple.400",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
                bg: "white",
              }}
              _placeholder={{ color: "gray.400" }}
            />
          </InputGroup>

          <HStack spacing={2} align="center">
            {/* Live/Paused filter */}
            <Menu closeOnSelect>
              <MenuButton
                as={Button}
                size="sm"
                variant={filterMode !== "all" ? "solid" : "outline"}
                colorScheme={filterMode !== "all" ? "purple" : "gray"}
                bg={filterMode !== "all" ? "purple.700" : "gray.100"}
                leftIcon={<Icon as={AppIcons.filter} boxSize={3} />}
                fontWeight="600"
                flexShrink={0}
                px={2}
                py={1}
              >
                {filterLabel[filterMode]}
              </MenuButton>
              <MenuList minW="160px" fontSize="sm">
                <MenuItem onClick={() => onFilterChange("all")} fontWeight={filterMode === "all" ? "600" : "normal"}>
                  All Workflows ({totalCount})
                </MenuItem>
                <MenuDivider />
                <MenuItem onClick={() => onFilterChange("live")} fontWeight={filterMode === "live" ? "600" : "normal"}>
                  Live Workflows ({liveCount})
                </MenuItem>
                <MenuItem
                  onClick={() => onFilterChange("paused")}
                  fontWeight={filterMode === "paused" ? "600" : "normal"}
                >
                  Paused Workflows ({totalCount - liveCount})
                </MenuItem>
              </MenuList>
            </Menu>

            {/* New Workflow — only when an analyst is selected */}
            {selectedAnalyst && (
              <Button
                variant="outline"
                size="sm"
                fontWeight="700"
                colorScheme="purple"
                leftIcon={<Icon as={AppIcons.plus} boxSize={3} />}
                onClick={onCreateNew}
                flexShrink={0}
                px={3}
              >
                New Workflow
              </Button>
            )}

            {/* Analyst Filter */}
            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                variant="outline"
                colorScheme="purple"
                bg="purple.50"
                borderColor="purple.50"
                isLoading={isLoadingAnalysts}
                _hover={{ bg: "purple.100", borderColor: "purple.100" }}
                borderRadius="full"
                borderWidth="1px"
                px={3}
                py={1}
              >
                <HStack spacing={2}>
                  <Icon as={AppIcons.analyst} boxSize={5} />
                  <Text fontSize="sm" color="fg.default" fontWeight="500" m={0} noOfLines={1}>
                    {selectedAnalyst ? selectedAnalyst.name : "All analysts"}
                  </Text>
                  <Icon as={AppIcons.chevronDown} boxSize={3.5} />
                </HStack>
              </MenuButton>
              <MenuList maxH="250px" overflowY="auto" minW="200px" borderColor="purple.200" boxShadow="lg">
                <MenuItem
                  fontSize="sm"
                  bg={!selectedAnalyst ? "purple.700" : "transparent"}
                  _hover={{ bg: !selectedAnalyst ? "purple.600" : "purple.50" }}
                  onClick={() => onAnalystSelect(null)}
                >
                  <HStack spacing={2}>
                    <Icon as={AppIcons.analyst} boxSize={5} color={!selectedAnalyst ? "white" : "purple.700"} />
                    <Text fontSize="sm" color={!selectedAnalyst ? "white" : "fg.default"} fontWeight="500" m={0}>
                      All analysts
                    </Text>
                  </HStack>
                </MenuItem>
                {analystsList.length > 0 && <MenuDivider />}
                {analystsList.length === 0 ? (
                  <MenuItem isDisabled fontSize="sm" color="purple.500">
                    No analysts found
                  </MenuItem>
                ) : (
                  analystsList.map(analyst => (
                    <MenuItem
                      key={analyst.id}
                      fontSize="sm"
                      bg={selectedAnalyst?.id === analyst.id ? "purple.700" : "transparent"}
                      _hover={{ bg: selectedAnalyst?.id === analyst.id ? "purple.600" : "purple.50" }}
                      onClick={() => onAnalystSelect(analyst)}
                    >
                      <HStack spacing={2}>
                        <Icon
                          as={AppIcons.analyst}
                          boxSize={5}
                          color={selectedAnalyst?.id === analyst.id ? "white" : "purple.700"}
                        />
                        <Text
                          fontSize="sm"
                          color={selectedAnalyst?.id === analyst.id ? "white" : "fg.default"}
                          fontWeight="500"
                          m={0}
                          noOfLines={1}
                        >
                          {analyst.name}
                        </Text>
                      </HStack>
                    </MenuItem>
                  ))
                )}
              </MenuList>
            </Menu>
          </HStack>
        </HStack>
      </Box>

      {/* Count row */}
      <Box maxW="900px" w="100%" mx="auto" mb={3} px={1}>
        <Text fontSize="sm" color="gray.500" fontWeight="600" m={0} textAlign="left">
          Showing {filteredJobs.length} {filteredJobs.length !== 1 ? "workflows" : "workflow"}
        </Text>
      </Box>

      {/* Job List */}
      <Box flex={1} minH={0} h="calc(100vh - 300px)">
        <JobList
          jobs={filteredJobs}
          isLoadingJobs={isLoadingJobs}
          analystsList={analystsList}
          onJobClick={onJobClick}
          onDefinitionClick={onDefinitionClick}
          onPastRunsClick={onPastRunsClick}
          onRefresh={onRefresh}
        />
      </Box>
    </>
  );
};

export default WorkflowList;
